'use client'

import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  ActivitySquare,
  Atom,
  BarChart2,
  Bug,
  Cable,
  ChartPie,
  Dna,
  FileCode,
  FileText,
  Folder,
  Gauge,
  Gift,
  GitFork,
  Home,
  Infinity,
  Key,
  Layers,
  LayoutDashboard,
  ListChecks,
  Logs,
  Microscope,
  PencilRuler,
  Search,
  Send,
  Settings,
  Shield,
  SmilePlus,
  Sparkles,
  SquareLibrary,
  Store,
  Tag,
  TrendingUp,
  User,
  Users,
  Waypoints
} from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  type AppCatalogListItem,
  getAppsList,
  getWorkflowsList,
  type LibrarySearchResult,
  searchLibrary,
  type WorkflowsListItem
} from '@/api/agent/agentApi'
import { FeedbackDialog } from '@/components/feedback-dialog'
import { SentryFeedbackTrigger } from '@/components/sentry-feedback-trigger'
import { data } from '@/components/sidebar/constanst'
import { OPEN_GLOBAL_NAV_COMMAND_EVENT } from '@/components/sidebar/global-nav-command-events'
import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { useAuth } from '@/contexts/AuthContext'
import { useNewsModal } from '@/contexts/NewsModalContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useActiveTeamOwner } from '@/hooks/use-active-team-owner'
import { useAdminRole } from '@/hooks/use-admin-role'
import { useFeatureAccessBatch } from '@/hooks/use-feature-access-batch'
import { toast } from '@/hooks/use-toast'
import { type AgentsThread, searchThreads } from '@/lib/agents-v2-api'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/utils/api-error-handler'
import { formatRelative, normalizeBackendTimestampToIsoUtc } from '@/utils/relative-time'
import { appendWorkspaceScopeToPath, normalizeTeamId } from '@/utils/workspace-scope'

const GLOBAL_COMMAND_KEY = 'g'

const navIconFallback: Record<string, LucideIcon> = {
  '/': Home,
  '/agents/home': Send,
  '/apps': Store,
  '/jobs': Logs,
  '/projects': LayoutDashboard,
  '/library': SquareLibrary,
  '/integrations': Cable,
  '/api-keys': Key,
  '/settings': Settings,
}

type CommandGroupName =
  | 'Main'
  | 'Agents'
  | 'Apps'
  | 'Jobs'
  | 'Agent Threads'
  | 'Library'
  | 'Settings'
  | 'Admin'

type GlobalCommandItem = {
  id: string
  title: string
  path: string
  icon: LucideIcon
  group: CommandGroupName
  parent?: string
  subtitle?: string
  rightHint?: string
  rightLabel?: string
  disabled?: boolean
  keywords?: string[]
  workspaceScoped?: boolean
  avatarUrl?: string | null
  appData?: CommandAppCatalogItem
}

type CommandWorkflowItem = WorkflowsListItem & {
  job_run_id?: string | null
}

type CommandAppCatalogItem = AppCatalogListItem & {
  launch_type?: 'route' | 'workflow_template' | string | null
  launch_target?: string | null
  has_access?: boolean | null
}

const APP_KEY_TO_MODEL: Record<string, string> = {
  'alphafold-2': 'monomer',
  'boltz-1': 'boltz',
  'boltz-2': 'boltz-2',
  'simple-fold': 'simplefold_100M',
  'esm-1b': 'esm1b',
  'openfold-3': 'openfold3',
  'chai-1': 'chai1',
  'intellifold': 'intellifold',
}

const EXTERNAL_APP_LINK_BY_KEY: Record<string, string> = {
  'agent-cli': 'https://docs.fastfold.ai/agents/cli',
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (target.isContentEditable) return true
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return Boolean(target.closest('[contenteditable="true"], [role="textbox"]'))
}

function shouldInvertAppLogoInDarkMode(appName?: string | null, appAvatarUrl?: string | null) {
  const name = String(appName || '').trim().toLowerCase()
  const avatar = String(appAvatarUrl || '').trim().toLowerCase()
  return name === 'boltzgen' || avatar.includes('boltzgen')
}

function appKindLabel(kind?: string | null) {
  const k = String(kind || '').trim().toLowerCase()
  if (k === 'model') return 'Model'
  if (k === 'workflow') return 'Workflow'
  if (k === 'tool') return 'Tool'
  return ''
}

function workflowIcon(type: string): LucideIcon {
  const t = String(type || '').toLowerCase()
  if (t === 'boltzgen_v1') return GitFork
  if (t === 'calvados_openmm_v1') return GitFork
  if (t === 'openmmdl_v1') return GitFork
  if (t === 'evolla_v1') return GitFork
  if (t === 'fold_job') return Atom
  if (t === 'peptide_drug_discovery') return Microscope
  if (t === 'denovo_sequence_generation') return PencilRuler
  return GitFork
}

function workflowLabel(type: string) {
  const t = String(type || '').toLowerCase()
  if (t === 'boltzgen_v1') return 'Workflows'
  if (t === 'calvados_openmm_v1') return 'Workflows'
  if (t === 'openmmdl_v1') return 'Workflows'
  if (t === 'evolla_v1') return 'Workflows'
  if (t === 'fold_job') return 'Fold'
  if (t === 'peptide_drug_discovery') return 'Properties'
  if (t === 'denovo_sequence_generation') return 'Design'
  return 'Job'
}

function isLikelySequenceInput(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) return false
  const compact = raw.replace(/\s+/g, '')
  return compact.length >= 20 && /^[ACDEFGHIKLMNPQRSTVWYBXZJUO]+$/i.test(compact)
}

function workflowDisplayTitle(item: WorkflowsListItem) {
  const label = String(item.label || '').trim()
  const type = String(item.workflow_type || '').toLowerCase()
  if (type === 'peptide_drug_discovery') return workflowLabel(item.workflow_type)
  if (label && !isLikelySequenceInput(label)) return label
  const targetName = String(item.target_name || item.target_id || '').trim()
  if (targetName) return targetName
  return workflowLabel(item.workflow_type)
}

function workflowDisplaySubtitle(item: WorkflowsListItem) {
  const label = String(item.label || '').trim()
  const type = String(item.workflow_type || '').toLowerCase()
  if (type === 'peptide_drug_discovery' && label) return label
  return workflowLabel(item.workflow_type)
}

function statusLabel(status?: string | null) {
  const s = String(status || '').trim().toUpperCase()
  if (!s) return ''
  return s
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

function parseTimestampMs(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) return 0
  const ms = Date.parse(normalizeBackendTimestampToIsoUtc(raw))
  return Number.isFinite(ms) ? ms : 0
}

function isSameLocalDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatLastModifiedLabel(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const normalized = normalizeBackendTimestampToIsoUtc(raw)
  const date = new Date(normalized)
  if (!Number.isFinite(date.getTime())) return ''
  const now = new Date()
  if (isSameLocalDay(date, now)) return 'Today'
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (isSameLocalDay(date, yesterday)) return 'Yesterday'
  return formatRelative(normalized)
}

function composeRightLabel(primary?: string | null, modifiedAt?: string | null) {
  const main = String(primary || '').trim()
  const modified = formatLastModifiedLabel(modifiedAt)
  if (main && modified) return `${main} · ${modified}`
  return main || modified
}

function compareByRecencyDesc(aMs: number, bMs: number) {
  return bMs - aMs
}

function workflowResultPath(item: CommandWorkflowItem) {
  const type = String(item.workflow_type || '').toLowerCase()
  if (type === 'boltzgen_v1') return `/workflow/composer/${encodeURIComponent(item.workflow_id)}`
  if (type === 'fold_job') {
    const runId = String(item.job_run_id || '').trim()
    if (runId) return `/sequences/${encodeURIComponent(item.workflow_id)}/${encodeURIComponent(runId)}`
    return '/jobs'
  }
  if (type === 'denovo_sequence_generation' || type === 'peptide_drug_discovery') {
    return `/tasks/results/${encodeURIComponent(item.workflow_id)}`
  }
  if (type === 'evolla_v1') return `/evolla/results/${encodeURIComponent(item.workflow_id)}`
  if (type === 'calvados_openmm_v1') return `/openmm/results/${encodeURIComponent(item.workflow_id)}`
  if (type === 'openmmdl_v1') return `/openmmdl/results/${encodeURIComponent(item.workflow_id)}`
  return '/jobs'
}

function libraryItemIcon(item: LibrarySearchResult): LucideIcon {
  if (item.type === 'folder') return Folder
  const ft = String(item.fileType || '').toLowerCase()
  if (ft === 'protein') return Waypoints
  if (ft === 'dna' || ft === 'rna') return Dna
  if (ft === 'ligand') return Infinity
  if (ft === 'complex') return Layers
  if (ft === 'code') return FileCode
  return FileText
}

function libraryResultPath(item: LibrarySearchResult): string {
  if (item.type === 'folder') return `/library/${encodeURIComponent(item.id)}`
  const ft = String(item.fileType || '').toLowerCase()
  if (ft === 'protein' || ft === 'dna' || ft === 'rna' || ft === 'complex') {
    return `/mol/${encodeURIComponent(item.id)}?from=library`
  }
  return `/code/${encodeURIComponent(item.id)}?from=library`
}

function libraryTypeLabel(item: LibrarySearchResult): string {
  if (item.type === 'folder') return 'Folder'
  const ft = String(item.fileType || '').trim()
  if (!ft) return 'File'
  return ft
    .split(/[_-]/g)
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

export function GlobalNavCommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [appsCatalog, setAppsCatalog] = useState<AppCatalogListItem[]>([])
  const [jobsResults, setJobsResults] = useState<CommandWorkflowItem[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [agentThreadResults, setAgentThreadResults] = useState<AgentsThread[]>([])
  const [agentThreadsLoading, setAgentThreadsLoading] = useState(false)
  const [libraryResults, setLibraryResults] = useState<LibrarySearchResult[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [reportBugDialogOpen, setReportBugDialogOpen] = useState(false)
  const jobsSearchSeqRef = useRef(0)
  const jobsQueryCacheRef = useRef<Map<string, CommandWorkflowItem[]>>(new Map())
  const threadsQueryCacheRef = useRef<Map<string, AgentsThread[]>>(new Map())
  const libraryQueryCacheRef = useRef<Map<string, LibrarySearchResult[]>>(new Map())
  const router = useRouter()
  const pathname = usePathname()
  const { user } = useAuth()
  const { openModal } = useNewsModal()
  const { activeTeamId, setActiveTeamId } = useWorkspace()
  const { canManageBilling } = useActiveTeamOwner()
  const { isAdmin } = useAdminRole()
  const { hasAccess: hasFeature } = useFeatureAccessBatch([
    'projects',
    'cloud_agent_ide_v2'
  ])
  const shortcutModifier = useMemo(() => {
    if (typeof navigator === 'undefined') return '⌘'
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? '⌘' : 'Ctrl'
  }, [])
  const quickNavShortcutHint = `${shortcutModifier} G`
  const quickAgentShortcutHint = `${shortcutModifier} K`
  const trimmedQuery = query.trim()
  const hasQuery = trimmedQuery.length > 0
  const hasAgentsV2Access = hasFeature('cloud_agent_ide_v2')

  const navigate = useCallback(
    (path: string, workspaceScoped = true) => {
      const nextPath = workspaceScoped ? appendWorkspaceScopeToPath(path, activeTeamId) : path
      router.push(nextPath)
      setOpen(false)
    },
    [activeTeamId, router]
  )
  const openWhatsNew = useCallback(() => {
    setOpen(false)
    openModal()
  }, [openModal])
  const openFeedback = useCallback(() => {
    setOpen(false)
    setFeedbackDialogOpen(true)
  }, [])
  const openReportBug = useCallback(() => {
    setOpen(false)
    setReportBugDialogOpen(true)
  }, [])

  const launchAppFromCommand = useCallback(
    async (app: CommandAppCatalogItem) => {
      const appKey = String(app.app_key || '').trim()
      if (!appKey) return

      if (app.has_access === false) {
        toast({ title: 'You do not have access to this app.', variant: 'destructive' })
        return
      }

      const externalUrl = EXTERNAL_APP_LINK_BY_KEY[appKey]
      if (externalUrl) {
        window.open(externalUrl, '_blank', 'noopener,noreferrer')
        setOpen(false)
        return
      }

      const scopedTeamId = normalizeTeamId(activeTeamId)
      const launchType = String(app.launch_type || 'route').toLowerCase()
      const launchTarget = String(app.launch_target || '').trim()

      if (launchType === 'route' && launchTarget) {
        const modelParam = APP_KEY_TO_MODEL[appKey]
        const target = modelParam ? `${launchTarget}?model=${modelParam}` : launchTarget
        router.push(appendWorkspaceScopeToPath(target, scopedTeamId))
        setOpen(false)
        return
      }

      if (launchType === 'workflow_template' && launchTarget) {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL
        if (!apiUrl) {
          toast({ title: 'Missing NEXT_PUBLIC_API_URL', variant: 'destructive' })
          return
        }

        try {
          const params = new URLSearchParams({ library_workspace: 'true' })
          if (scopedTeamId) params.set('team_id', scopedTeamId)
          const runName = `My ${app.name} Run ${new Date().toISOString().slice(0, 10)}`
          const res = await fetchWithAuth(`${apiUrl}/v1/workflows/graph/add?${params.toString()}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ name: runName, workflow_name: launchTarget }),
          })
          if (!res.ok) {
            throw new Error(`Failed to create workflow: ${res.status}`)
          }
          const json = (await res.json()) as { workflow_id?: string }
          const workflowId = String(json.workflow_id || '').trim()
          if (!workflowId) throw new Error('Invalid response: missing workflow_id')

          setActiveTeamId(scopedTeamId)
          router.push(
            appendWorkspaceScopeToPath(
              `/workflow/composer/${workflowId}?template=${encodeURIComponent(appKey)}&show_presets=true`,
              scopedTeamId
            )
          )
          setOpen(false)
          return
        } catch {
          toast({ title: `Could not launch ${app.name}`, variant: 'destructive' })
          return
        }
      }

      // Fallback for incomplete app metadata.
      router.push(appendWorkspaceScopeToPath(`/jobs?app_key=${encodeURIComponent(appKey)}`, scopedTeamId))
      setOpen(false)
    },
    [activeTeamId, router, setActiveTeamId]
  )

  useEffect(() => {
    if (!open) return
    if (appsCatalog.length > 0) return
    const controller = new AbortController()
    void (async () => {
      const apps = await getAppsList({ limit: 200, signal: controller.signal })
      setAppsCatalog(apps)
    })()
    return () => controller.abort()
  }, [appsCatalog.length, open])

  useEffect(() => {
    if (!open) return
    if (!trimmedQuery) {
      setJobsResults([])
      setJobsLoading(false)
      return
    }
    const cached = jobsQueryCacheRef.current.get(trimmedQuery)
    if (cached) {
      setJobsResults(cached)
      setJobsLoading(false)
      return
    }
    const requestSeq = ++jobsSearchSeqRef.current
    setJobsLoading(true)
    const timeout = window.setTimeout(async () => {
      const response = await getWorkflowsList({
        limit: 12,
        q: trimmedQuery
      })
      if (requestSeq !== jobsSearchSeqRef.current) return
      const next = [...((response?.data || []) as CommandWorkflowItem[])].sort((a, b) => {
        const byRecency = compareByRecencyDesc(
          parseTimestampMs(a.updated_at || a.created_at),
          parseTimestampMs(b.updated_at || b.created_at)
        )
        if (byRecency !== 0) return byRecency
        return String(a.label || a.target_name || a.workflow_id || '').localeCompare(
          String(b.label || b.target_name || b.workflow_id || '')
        )
      })
      jobsQueryCacheRef.current.set(trimmedQuery, next)
      setJobsResults(next)
      setJobsLoading(false)
    }, 250)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [open, trimmedQuery])

  useEffect(() => {
    if (!open) return
    if (!hasAgentsV2Access) {
      setAgentThreadResults([])
      setAgentThreadsLoading(false)
      return
    }
    if (trimmedQuery.length < 2) {
      setAgentThreadResults([])
      setAgentThreadsLoading(false)
      return
    }
    if (!user) {
      setAgentThreadResults([])
      setAgentThreadsLoading(false)
      return
    }
    const cacheKey = `${activeTeamId || 'personal'}:${trimmedQuery}`
    const cached = threadsQueryCacheRef.current.get(cacheKey)
    if (cached) {
      setAgentThreadResults(cached)
      setAgentThreadsLoading(false)
      return
    }
    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      setAgentThreadsLoading(true)
      searchThreads(user, activeTeamId, trimmedQuery, {
        limit: 20,
        signal: controller.signal
      })
        .then((res) => {
          const next = [...(res?.results ?? [])].sort((a, b) => {
            const byRecency = compareByRecencyDesc(
              parseTimestampMs(a.last_message_at || a.updated_at || a.created_at),
              parseTimestampMs(b.last_message_at || b.updated_at || b.created_at)
            )
            if (byRecency !== 0) return byRecency
            return String(a.title || a.id || '').localeCompare(String(b.title || b.id || ''))
          })
          threadsQueryCacheRef.current.set(cacheKey, next)
          setAgentThreadResults(next)
        })
        .catch(() => {
          if (!controller.signal.aborted) setAgentThreadResults([])
        })
        .finally(() => {
          if (!controller.signal.aborted) setAgentThreadsLoading(false)
        })
    }, 300)
    return () => {
      controller.abort()
      window.clearTimeout(timeout)
    }
  }, [activeTeamId, hasAgentsV2Access, open, trimmedQuery, user])

  useEffect(() => {
    if (!open) return
    if (!trimmedQuery) {
      setLibraryResults([])
      setLibraryLoading(false)
      return
    }
    const cached = libraryQueryCacheRef.current.get(trimmedQuery)
    if (cached) {
      setLibraryResults(cached)
      setLibraryLoading(false)
      return
    }
    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      setLibraryLoading(true)
      searchLibrary(trimmedQuery, { signal: controller.signal })
        .then((res) => {
          const next = [...(res?.results ?? [])].sort((a, b) => {
            const byRecency = compareByRecencyDesc(
              parseTimestampMs(a.updatedAt || a.createdAt),
              parseTimestampMs(b.updatedAt || b.createdAt)
            )
            if (byRecency !== 0) return byRecency
            return String(a.name || a.id || '').localeCompare(String(b.name || b.id || ''))
          })
          libraryQueryCacheRef.current.set(trimmedQuery, next)
          setLibraryResults(next)
        })
        .finally(() => {
          if (!controller.signal.aborted) setLibraryLoading(false)
        })
    }, 250)
    return () => {
      controller.abort()
      window.clearTimeout(timeout)
    }
  }, [open, trimmedQuery])

  const commandFilter = useCallback((value: string, search: string, keywords?: string[]) => {
    const query = search.trim().toLowerCase()
    if (!query) return 1
    const haystack = `${value} ${(keywords ?? []).join(' ')}`.toLowerCase()
    if (!haystack) return 0
    if (haystack.includes(query)) return 2
    const terms = query.split(/\s+/).filter(Boolean)
    if (terms.length > 1 && terms.every((term) => haystack.includes(term))) return 1
    return 0
  }, [])

  const items = useMemo<GlobalCommandItem[]>(() => {
    const filteredMain = data.navMain
      .filter((item) => !item.disabled)
      .filter((item) => {
        if (!item.requiredFeatures || item.requiredFeatures.length === 0) return true
        return item.requiredFeatures.every((feature) => hasFeature(feature))
      })

    const mainItems: GlobalCommandItem[] = filteredMain.map((item) => ({
      id: `main:${item.url}`,
      title: item.title,
      path: item.url === '/settings' ? (activeTeamId ? `/teams/${activeTeamId}/settings` : '/settings') : item.url,
      icon: (item.icon as LucideIcon) ?? navIconFallback[item.url] ?? Home,
      group: 'Main',
      workspaceScoped: true,
      keywords: [item.title, 'sidebar', 'navigation', item.url]
    }))

    const agentItems: GlobalCommandItem[] = []
    if (hasAgentsV2Access) {
      agentItems.push(
        {
          id: 'agents:new-agent',
          title: 'New Agent',
          parent: 'Agents',
          subtitle: 'Agents',
          rightHint: quickAgentShortcutHint,
          path: '/agents/home',
          icon: Send,
          group: 'Agents',
          workspaceScoped: true,
          keywords: ['create agent', 'chat', 'assistant']
        },
        {
          id: 'agents:skills',
          title: 'Skills',
          parent: 'Agents',
          subtitle: 'Agents',
          path: '/agents/skills',
          icon: Sparkles,
          group: 'Agents',
          workspaceScoped: true,
          keywords: ['agents skills', 'prompts', 'tools']
        }
      )
    }

    const appsItemsSource = [...appsCatalog]
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    const appsItems: GlobalCommandItem[] = (hasQuery ? appsItemsSource : appsItemsSource.slice(0, 24))
      .map((app) => ({
        id: `apps:${app.app_key}`,
        title: app.name,
        subtitle: 'Apps',
        path: '/apps',
        icon: Tag,
        group: 'Apps',
        avatarUrl: app.avatar_url ?? null,
        rightLabel: appKindLabel(app.kind),
        workspaceScoped: true,
        keywords: [app.app_key, app.kind || '', 'apps', 'launch app'],
        appData: app as CommandAppCatalogItem
      }))

    const jobsItems: GlobalCommandItem[] = hasQuery
      ? jobsResults.map((job) => ({
          id: `jobs:${job.workflow_id}`,
          title: workflowDisplayTitle(job),
          parent: workflowDisplaySubtitle(job),
          path: workflowResultPath(job),
          icon: workflowIcon(job.workflow_type),
          group: 'Jobs',
          rightLabel: composeRightLabel(statusLabel(job.status), job.updated_at || job.created_at),
          avatarUrl: job.app_avatar_url ?? null,
          workspaceScoped: true,
          keywords: [
            job.workflow_id,
            job.workflow_type,
            job.status,
            job.app_name,
            job.target_name,
            job.label,
            trimmedQuery
          ].filter(Boolean) as string[]
        }))
      : []

    const agentThreadsItems: GlobalCommandItem[] = hasQuery && hasAgentsV2Access
      ? agentThreadResults.map((thread) => ({
          id: `agent-thread:${thread.id}`,
          title: String(thread.title || '').trim() || 'Untitled thread',
          parent: String(thread.project_name || '').trim() || undefined,
          path: `/agents/${encodeURIComponent(thread.id)}`,
          icon: Send,
          group: 'Agent Threads',
          rightLabel: composeRightLabel(
            statusLabel(String(thread.status || '')),
            thread.last_message_at || thread.updated_at || thread.created_at
          ),
          workspaceScoped: true,
          keywords: [
            thread.id,
            thread.title,
            thread.project_name,
            thread.status,
            'agent thread',
            'chat',
            trimmedQuery
          ].filter(Boolean) as string[]
        }))
      : []

    const libraryItems: GlobalCommandItem[] = hasQuery
      ? libraryResults.map((item) => ({
          id: `library:${item.id}`,
          title: item.name,
          parent: item.parentName || undefined,
          path: libraryResultPath(item),
          icon: libraryItemIcon(item),
          group: 'Library',
          rightLabel: composeRightLabel(libraryTypeLabel(item), item.updatedAt || item.createdAt),
          workspaceScoped: true,
          keywords: [
            item.id,
            item.name,
            item.type,
            item.fileType,
            item.parentName,
            'library',
            trimmedQuery
          ].filter(Boolean) as string[]
        }))
      : []

    if (hasQuery && jobsLoading && jobsItems.length === 0) {
      jobsItems.push({
        id: 'jobs:loading',
        title: 'Searching jobs...',
        path: '/jobs',
        icon: Logs,
        group: 'Jobs',
        disabled: true,
        workspaceScoped: true,
        rightLabel: 'Loading'
      })
    } else if (hasQuery && !jobsLoading && jobsItems.length === 0) {
      jobsItems.push({
        id: 'jobs:empty',
        title: 'No jobs found',
        path: '/jobs',
        icon: Logs,
        group: 'Jobs',
        disabled: true,
        workspaceScoped: true,
        rightLabel: '0',
        keywords: [trimmedQuery]
      })
    }

    if (hasQuery && hasAgentsV2Access && agentThreadsLoading && agentThreadsItems.length === 0) {
      agentThreadsItems.push({
        id: 'agent-threads:loading',
        title: 'Searching agent threads...',
        path: '/agents/home',
        icon: Send,
        group: 'Agent Threads',
        disabled: true,
        workspaceScoped: true,
        rightLabel: 'Loading'
      })
    } else if (hasQuery && hasAgentsV2Access && !agentThreadsLoading && agentThreadsItems.length === 0) {
      agentThreadsItems.push({
        id: 'agent-threads:empty',
        title: 'No agent threads found',
        path: '/agents/home',
        icon: Send,
        group: 'Agent Threads',
        disabled: true,
        workspaceScoped: true,
        rightLabel: '0',
        keywords: [trimmedQuery]
      })
    }

    if (hasQuery && libraryLoading && libraryItems.length === 0) {
      libraryItems.push({
        id: 'library:loading',
        title: 'Searching library...',
        path: '/library',
        icon: SquareLibrary,
        group: 'Library',
        disabled: true,
        workspaceScoped: true,
        rightLabel: 'Loading'
      })
    } else if (hasQuery && !libraryLoading && libraryItems.length === 0) {
      libraryItems.push({
        id: 'library:empty',
        title: 'No library items found',
        path: '/library',
        icon: SquareLibrary,
        group: 'Library',
        disabled: true,
        workspaceScoped: true,
        rightLabel: '0',
        keywords: [trimmedQuery]
      })
    }

    const settingsItems: GlobalCommandItem[] = [
      {
        id: 'settings:profile',
        title: activeTeamId ? 'Workspace Settings' : 'Profile',
        parent: 'Settings',
        subtitle: 'Settings',
        path: activeTeamId ? `/teams/${activeTeamId}/settings` : '/settings',
        icon: activeTeamId ? Settings : User,
        group: 'Settings',
        workspaceScoped: true,
        keywords: ['account', 'settings', 'profile', 'workspace']
      }
    ]

    if (activeTeamId) {
      settingsItems.push({
        id: 'settings:members',
        title: 'Members',
        parent: 'Settings',
        subtitle: 'Settings',
        path: `/teams/${activeTeamId}/members`,
        icon: Users,
        group: 'Settings',
        workspaceScoped: true,
        keywords: ['team', 'members', 'permissions']
      })
    }

    if (canManageBilling) {
      settingsItems.push(
        {
          id: 'settings:billing',
          title: 'Billing',
          parent: 'Settings',
          subtitle: 'Settings',
          path: '/billing',
          icon: BarChart2,
          group: 'Settings',
          workspaceScoped: true,
          keywords: ['invoices', 'payments', 'plan']
        },
        {
          id: 'settings:invoices',
          title: 'Invoices',
          parent: 'Settings',
          subtitle: 'Settings',
          path: '/invoices',
          icon: FileText,
          group: 'Settings',
          workspaceScoped: true,
          keywords: ['billing history', 'receipt']
        },
        {
          id: 'settings:quotas',
          title: 'Quotas',
          parent: 'Settings',
          subtitle: 'Settings',
          path: '/quota',
          icon: Gauge,
          group: 'Settings',
          workspaceScoped: true,
          keywords: ['usage limits', 'quota']
        },
        {
          id: 'settings:usage',
          title: 'Usage',
          parent: 'Settings',
          subtitle: 'Settings',
          path: '/usage',
          icon: ChartPie,
          group: 'Settings',
          workspaceScoped: true,
          keywords: ['metrics', 'consumption']
        }
      )
    }

    const adminItems: GlobalCommandItem[] = isAdmin
      ? [
          {
            id: 'admin:home',
            title: 'Admin',
            parent: 'Admin',
            path: '/admin',
            icon: Shield,
            group: 'Admin',
            workspaceScoped: false,
            keywords: ['admin panel']
          },
          {
            id: 'admin:dispatch',
            title: 'Dispatch Queue',
            parent: 'Admin',
            path: '/admin/dispatch',
            icon: ActivitySquare,
            group: 'Admin',
            workspaceScoped: false,
            keywords: ['dispatch', 'queue']
          },
          {
            id: 'admin:executions',
            title: 'Executions',
            parent: 'Admin',
            path: '/admin/dispatch/executions',
            icon: ListChecks,
            group: 'Admin',
            workspaceScoped: false,
            keywords: ['executions', 'jobs', 'workflows', 'dispatch']
          },
          {
            id: 'admin:kpis',
            title: 'KPIs',
            parent: 'Admin',
            path: '/admin/kpis',
            icon: TrendingUp,
            group: 'Admin',
            workspaceScoped: false,
            keywords: ['analytics', 'kpis']
          },
          {
            id: 'admin:monitor',
            title: 'Live Monitor',
            parent: 'Admin',
            path: '/admin/monitor',
            icon: Activity,
            group: 'Admin',
            workspaceScoped: false,
            keywords: ['monitor', 'wallboard', 'live', 'metrics', 'queue']
          }
        ]
      : []

    return [
      ...mainItems,
      ...agentItems,
      ...appsItems,
      ...jobsItems,
      ...agentThreadsItems,
      ...libraryItems,
      ...settingsItems,
      ...adminItems
    ]
  }, [
    activeTeamId,
    agentThreadResults,
    agentThreadsLoading,
    appsCatalog,
    canManageBilling,
    hasFeature,
    hasAgentsV2Access,
    hasQuery,
    isAdmin,
    jobsLoading,
    jobsResults,
    libraryLoading,
    libraryResults,
    quickAgentShortcutHint
  ])

  const groupedItems = useMemo(() => {
    const order: CommandGroupName[] = hasQuery
      ? ['Jobs', 'Agent Threads', 'Library', 'Apps', 'Main', 'Agents', 'Settings', 'Admin']
      : ['Main', 'Agents', 'Apps', 'Settings', 'Admin']
    return order
      .map((name) => ({
        name,
        items: items.filter((item) => item.group === name)
      }))
      .filter((group) => group.items.length > 0)
  }, [hasQuery, items])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const hasModifier = event.metaKey || event.ctrlKey
      const key = event.key.toLowerCase()
      const isShortcutKey = key === GLOBAL_COMMAND_KEY
      if (!hasModifier || event.altKey || event.shiftKey || !isShortcutKey) return
      if (isEditableTarget(event.target)) return
      event.preventDefault()
      setOpen((prev) => !prev)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    const onOpenCommand = () => setOpen(true)
    window.addEventListener(OPEN_GLOBAL_NAV_COMMAND_EVENT, onOpenCommand)
    return () => window.removeEventListener(OPEN_GLOBAL_NAV_COMMAND_EVENT, onOpenCommand)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    if (open) return
    setQuery('')
    setJobsResults([])
    setAgentThreadResults([])
    setLibraryResults([])
    setJobsLoading(false)
    setAgentThreadsLoading(false)
    setLibraryLoading(false)
  }, [open])

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      modal={false}
      overlayClassName="bg-transparent pointer-events-none"
      contentClassName="top-[12vh] w-[min(920px,calc(100vw-1rem))] max-w-[920px] translate-y-0 overflow-hidden rounded-[24px] border border-border/60 bg-background/70 p-0 shadow-[0_20px_70px_rgba(0,0,0,0.35)] backdrop-blur-2xl supports-[backdrop-filter]:bg-background/55 [&>button]:hidden"
      commandProps={{
        filter: commandFilter,
        className:
          'bg-transparent [&_[cmdk-input-wrapper]]:border-border/60 [&_[cmdk-input-wrapper]]:px-4 [&_[cmdk-group]]:px-2 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground/90 [&_[cmdk-item]]:rounded-xl [&_[cmdk-item]]:px-4 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item][data-selected=true]]:bg-accent/75'
      }}
    >
      <div className="relative">
        <CommandInput
          icon={<Search className="mr-2 h-4 w-4 text-muted-foreground/80" />}
          placeholder="Search Anything ...."
          value={query}
          onValueChange={setQuery}
          className="h-14 pr-28 text-base"
        />
        <div className="pointer-events-none absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2 text-xs text-muted-foreground">
          <span className="hidden sm:inline">Quick Nav</span>
          <kbd className="rounded-md border border-border/70 bg-background/65 px-1.5 py-0.5 font-mono text-[11px]">
            {quickNavShortcutHint}
          </kbd>
        </div>
      </div>
      <CommandList className="max-h-[500px]">
        <CommandEmpty>No matching page found.</CommandEmpty>
        {groupedItems.map((group) => (
          <Fragment key={group.name}>
            <CommandGroup heading={group.name}>
              {group.items.map((item) => {
                const Icon = item.icon
                const keywords = [item.group, item.parent, item.subtitle, item.path, ...(item.keywords ?? [])]
                  .filter(Boolean) as string[]
                const commandValue = `${item.title} ${item.id}`
                return (
                  <CommandItem
                    key={item.id}
                    value={commandValue}
                    keywords={keywords}
                    disabled={item.disabled}
                    onSelect={() => {
                      if (item.disabled) return
                      if (item.group === 'Apps' && item.appData) {
                        void launchAppFromCommand(item.appData)
                        return
                      }
                      navigate(item.path, item.workspaceScoped)
                    }}
                  >
                    {item.avatarUrl ? (
                      <img
                        src={item.avatarUrl}
                        alt={item.title}
                        className={cn(
                          'mr-2 h-4 w-4 rounded-sm object-cover',
                          shouldInvertAppLogoInDarkMode(item.title, item.avatarUrl) && 'dark:invert'
                        )}
                      />
                    ) : (
                      <Icon className="mr-2 h-4 w-4" />
                    )}
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-sm">{item.title}</span>
                      {item.parent && item.parent !== item.title ? (
                        <span className="truncate text-xs text-muted-foreground">{item.parent}</span>
                      ) : null}
                    </div>
                    {item.rightHint ? (
                      <span className="ml-3 inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="hidden sm:inline">Quick Cmd</span>
                        <kbd className="rounded-md border border-border/70 bg-background/65 px-1.5 py-0.5 font-mono text-[11px]">
                          {item.rightHint}
                        </kbd>
                      </span>
                    ) : item.rightLabel ? (
                      <span className="ml-3 shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
                        {item.rightLabel}
                      </span>
                    ) : null}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </Fragment>
        ))}
      </CommandList>
      <div className="flex items-center justify-between gap-2 border-t border-border/60 bg-background/55 px-4 py-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openWhatsNew}
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Gift className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">What&apos;s New</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openFeedback}
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <SmilePlus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Give Feedback</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openReportBug}
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Bug className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Report Bug</span>
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Open Page</span>
          <kbd className="rounded-md border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono text-[11px]">↵</kbd>
        </div>
      </div>
      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        hideTrigger
      />
      <SentryFeedbackTrigger
        open={reportBugDialogOpen}
        onOpenChange={setReportBugDialogOpen}
        hideTrigger
      />
    </CommandDialog>
  )
}
